import React, { useState, useEffect } from 'react';
import { 
  Sparkles, Plus, Trash2, Clock, TrendingUp, Calendar, Bell, Edit2, Check, X,
  FileText, ExternalLink, ChevronDown, ChevronUp, MessageSquare, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast, ToastContainer } from '../componets/Toast';
import API_URL from '../config/API_URL';
import FloatingBriefingChat from '../componets/FloatingBriefingChat';
import PaywallModal from '../subscription/PaywallModal';

const TopicIntelligence = () => {
  const [topics, setTopics] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const [showChat, setShowChat] = useState(true);
  const [contacts, setContacts] = useState([]);
  
  // ✨ Paywall state (following calendar pattern)
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeatureInfo, setPaywallFeatureInfo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    topic_name: '',
    description: '',
    frequency: 'daily',
    time_of_day: '16:00',
    is_active: true,
    keywords: []
  });
  
  const [keywordInput, setKeywordInput] = useState('');
  
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    headerLeft: {
      flex: 1,
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '1.5rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    input: {
      width: '96%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
    },
    textarea: {
      width: '96%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    keywordContainer: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      marginBottom: '0.75rem',
    },
    keyword: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.875rem',
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      fontSize: '0.875rem',
      color: '#1e40af',
      fontWeight: 500,
    },
    keywordRemove: {
      cursor: 'pointer',
      color: '#ef4444',
      display: 'flex',
      alignItems: 'center',
    },
    keywordInput: {
      display: 'flex',
      gap: '0.5rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
      marginTop: '1.5rem',
    },
    primaryButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    secondaryButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#64748b',
    },
    topicsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
    topicCard: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      transition: 'all 200ms',
    },
    topicHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    topicLeft: {
      flex: 1,
    },
    topicTitle: {
      fontSize: '1.4rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    topicDescription: {
      fontSize: '0.875rem',
      color: '#64748b',
      lineHeight: 1.5,
      marginBottom: '1rem',
    },
    topicMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '1rem',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: '#475569',
    },
    topicActions: {
      display: 'flex',
      gap: '0.5rem',
      flexShrink: 0,
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 200ms',
      background: '#f8fafc',
      color: '#64748b',
    },
    activeIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.875rem',
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    activeTrue: {
      background: '#dcfce7',
      color: '#166534',
    },
    activeFalse: {
      background: '#fee2e2',
      color: '#991b1b',
    },
    hint: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.5rem',
    },
    briefingsSection: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #e2e8f0',
    },
    briefingsSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    briefingsTitle: {
      fontSize: '1.1rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    expandButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#f8fafc',
      color: '#64748b',
    },
    latestBriefing: {
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '1.25rem',
      border: '1px solid #e2e8f0',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    briefingPreviewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.75rem',
    },
    briefingDate: {
      fontSize: '0.75rem',
      color: '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    briefingSummary: {
      fontSize: '0.875rem',
      color: '#475569',
      lineHeight: 1.6,
      marginBottom: '0.75rem',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    keyPointsPreview: {
      fontSize: '0.875rem',
      color: '#64748b',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    unreadBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: '#2563eb',
      color: 'white',
      fontSize: '0.7rem',
      fontWeight: 700,
    },
    briefingsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    briefingItem: {
      background: '#ffffff',
      borderRadius: '10px',
      padding: '1rem',
      border: '1px solid #e2e8f0',
      cursor: 'pointer',
      transition: 'all 200ms',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    briefingItemLeft: {
      flex: 1,
    },
    briefingItemTitle: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    briefingItemDate: {
      fontSize: '0.75rem',
      color: '#94a3b8',
    },
    briefingItemArrow: {
      color: '#cbd5e1',
      transition: 'all 200ms',
    },
    noBriefings: {
      textAlign: 'center',
      padding: '2rem',
      color: '#94a3b8',
      fontSize: '0.875rem',
      fontStyle: 'italic',
    },
    empty: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#475569',
      background: '#ffffff',
      borderRadius: '16px',
      border: '2px dashed #e2e8f0',
    },
    emptyIcon: {
      marginBottom: '1rem',
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      marginBottom: '0.5rem',
      color: '#0f172a',
    },
    emptyText: {
      fontSize: '1rem',
      marginBottom: '1.5rem',
    },
    // Full briefing modal styles
    briefingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
    },
    briefingModal: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      maxWidth: '900px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.2)',
      position: 'relative',
      marginRight: showChat ? '460px' : '0',
      transition: 'margin-right 300ms ease-in-out',
    },
    closeButton: {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 200ms',
      background: '#f8fafc',
      color: '#64748b',
    },
    detailHeader: {
      marginBottom: '2rem',
      paddingBottom: '1.5rem',
      borderBottom: '2px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '1rem',
      paddingRight: '3rem',
    },
    detailHeaderContent: {
      flex: 1,
      minWidth: '300px',
    },
    detailTopicName: {
      fontSize: '1.75rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    detailDate: {
      fontSize: '0.95rem',
      color: '#64748b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    generateMessageButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: showChat 
        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: showChat 
        ? '0 4px 12px rgba(16, 185, 129, 0.4)' 
        : '0 4px 12px rgba(102, 126, 234, 0.4)',
      height: 'fit-content',
    },
    section: {
      marginBottom: '2rem',
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    summary: {
      fontSize: '1rem',
      color: '#0f172a',
      lineHeight: 1.8,
      marginBottom: '2rem',
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    keyPoints: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    keyPoint: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: 1.6,
      marginBottom: '0.75rem',
      paddingLeft: '1.5rem',
      position: 'relative',
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
    keyPointBullet: {
      position: 'absolute',
      left: 0,
      top: '0.5rem',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#2563eb',
    },
    sources: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    sourceItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '10px',
      border: '1px solid #e2e8f0',
      transition: 'all 200ms',
      textDecoration: 'none',
      color: 'inherit',
    },
    sourceIcon: {
      color: '#2563eb',
      flexShrink: 0,
    },
    sourceContent: {
      flex: 1,
    },
    sourceTitle: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    sourceDate: {
      fontSize: '0.75rem',
      color: '#94a3b8',
    },
    analysis: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: 1.8,
      padding: '1.5rem',
      background: '#eff6ff',
      borderRadius: '12px',
      border: '1px solid #bfdbfe',
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text',
    },
  };

  useEffect(() => {
    loadData();
  }, []);

  // ✨ POST-PURCHASE RESTORATION EFFECT (following calendar pattern exactly)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromPayment = urlParams.get('from_payment');
    
    // ✨ READ FROM LOCALSTORAGE (persists across navigation)
    const savedTopicData = localStorage.getItem('topic_briefing_pending_data');
    const isPending = localStorage.getItem('topic_briefing_pending');
    
    console.log('🔍 Post-payment check:', { fromPayment, savedTopicData, isPending });
    
    if (fromPayment === 'topic_briefing' && savedTopicData && isPending === 'true') {
      console.log('✅ Restoring topic creation:', savedTopicData);
      
      // Clear localStorage first
      localStorage.removeItem('topic_briefing_pending_data');
      localStorage.removeItem('topic_briefing_pending');
      
      // Clear URL parameter
      window.history.replaceState({}, '', '/dashboard/topic-scheduler');
      
      // Restore form data
      const parsedData = JSON.parse(savedTopicData);
      setFormData(parsedData);
      setShowAddForm(true);
      
      // Show success message
      showSuccess('Credits added! Creating your topic...');
      
      // Auto-submit after delay to ensure form is open
      setTimeout(() => {
        console.log('🚀 Auto-submitting topic creation');
        handleSubmitWithData(parsedData);
      }, 800);
    }
  }, []); // Empty array - only run once on mount

  const loadData = async () => {
    try {
      await Promise.all([
        loadTopics(),
        loadBriefings(),
        loadContacts()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTopics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/topic-scheduler/topics`, {
        credentials: 'include'
      });
      const data = await response.json();
      setTopics(data.topics || []);
    } catch (error) {
      console.error('Failed to load topics:', error);
      showError('Failed to load topics');
    }
  };

  const loadBriefings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/topic-scheduler/briefings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setBriefings(data.briefings || []);
    } catch (error) {
      console.error('Failed to load briefings:', error);
      showError('Failed to load briefings');
    }
  };

  const loadContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const getBriefingsForTopic = (topicId) => {
    return briefings.filter(b => b.topic_id === topicId);
  };

  const getLatestBriefing = (topicId) => {
    const topicBriefings = getBriefingsForTopic(topicId);
    return topicBriefings.length > 0 ? topicBriefings[0] : null;
  };

  const getUnreadCount = (topicId) => {
    return getBriefingsForTopic(topicId).filter(b => !b.is_read).length;
  };

  const toggleTopicExpanded = (topicId) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedTopics(newExpanded);
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(k => k !== keyword)
    });
  };

  const handleSubmitWithData = async (dataToSubmit = formData) => {
    if (!dataToSubmit.topic_name.trim()) {
      showWarning('Please enter a topic name');
      return;
    }

    try {
      const url = editingTopic
        ? `${API_URL}/api/topic-scheduler/topics/${editingTopic.id}`
        : `${API_URL}/api/topic-scheduler/topics`;
      
      const method = editingTopic ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSubmit)
      });

      const data = await response.json();

      // ✨ CHECK FOR PAYWALL (following calendar pattern exactly)
      if (response.status === 403 && data.requires_purchase) {
        // ✨ SAVE TO LOCALSTORAGE (persists across navigation)
        localStorage.setItem('topic_briefing_pending_data', JSON.stringify(dataToSubmit));
        localStorage.setItem('topic_briefing_pending', 'true');
        
        console.log('💾 Saved topic data to localStorage, showing paywall');
        
        // Show paywall (NO data passed - following your pattern!)
        setPaywallFeatureInfo(data);
        setShowPaywall(true);
        return;
      }

      if (response.ok) {
        showSuccess(editingTopic ? 'Topic updated!' : 'Topic created!');
        setShowAddForm(false);
        setEditingTopic(null);
        resetForm();
        loadTopics();
      } else {
        showError(data.error || 'Failed to save topic');
      }
    } catch (error) {
      console.error('Failed to save topic:', error);
      showError('Failed to save topic');
    }
  };

  const handleSubmit = async () => {
    await handleSubmitWithData(formData);
  };

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    setFormData({
      topic_name: topic.topic_name,
      description: topic.description || '',
      frequency: topic.frequency,
      time_of_day: topic.time_of_day,
      is_active: topic.is_active,
      keywords: topic.keywords || []
    });
    setShowAddForm(true);
  };

  const handleDelete = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;

    try {
      const response = await fetch(`${API_URL}/api/topic-scheduler/topics/${topicId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showSuccess('Topic deleted');
        loadTopics();
      } else {
        showError('Failed to delete topic');
      }
    } catch (error) {
      console.error('Failed to delete topic:', error);
      showError('Failed to delete topic');
    }
  };

  const toggleActive = async (topic) => {
    try {
      const response = await fetch(`${API_URL}/api/topic-scheduler/topics/${topic.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...topic,
          is_active: !topic.is_active
        })
      });

      if (response.ok) {
        showSuccess(topic.is_active ? 'Topic paused' : 'Topic activated');
        loadTopics();
      } else {
        showError('Failed to update topic');
      }
    } catch (error) {
      console.error('Failed to toggle topic:', error);
      showError('Failed to update topic');
    }
  };

  const handleBriefingClick = async (briefing) => {
    setSelectedBriefing(briefing);
    
    // Mark as read
    if (!briefing.is_read) {
      try {
        await fetch(`${API_URL}/api/topic-scheduler/briefings/${briefing.id}/mark-read`, {
          method: 'POST',
          credentials: 'include'
        });
        
        // Update local state
        setBriefings(briefings.map(b =>
          b.id === briefing.id ? { ...b, is_read: true } : b
        ));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleOpenChat = (briefing) => {
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
  };

  const resetForm = () => {
    setFormData({
      topic_name: '',
      description: '',
      frequency: 'daily',
      time_of_day: '16:00',
      is_active: true,
      keywords: []
    });
    setKeywordInput('');
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingTopic(null);
    resetForm();
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          Loading your intelligence dashboard...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* ✨ PAYWALL MODAL (following calendar pattern - NO pendingData prop!) */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          // ✨ Optional: restore from localStorage if they close without buying
          const savedData = localStorage.getItem('topic_briefing_pending_data');
          if (savedData) {
            setFormData(JSON.parse(savedData));
            // Keep it in localStorage in case they try again
          }
        }}
        featureInfo={paywallFeatureInfo}
        sourcePage="topic_scheduler"
        onPurchaseComplete={() => {
          setShowPaywall(false);
          // Topic creation will happen automatically in useEffect after redirect
        }}
      />

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Topic Intelligence</h1>
          <p style={styles.subtitle}>
            Automated briefings on topics you care about. Set up topics to receive AI-powered insights.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
          }}
        >
          <Plus size={20} />
          Add Topic
        </button>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={styles.card}
          >
            <h2 style={{ ...styles.title, fontSize: '1.5rem', marginBottom: '1.5rem' }}>
              {editingTopic ? 'Edit Topic' : 'Add New Topic'}
            </h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Topic Name *</label>
              <input
                type="text"
                value={formData.topic_name}
                onChange={(e) => setFormData({ ...formData, topic_name: e.target.value })}
                placeholder="e.g., Bitcoin Price Updates, AI Industry News, Tech Earnings"
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={styles.hint}>Give your topic a clear, descriptive name</p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What specific aspects of this topic are you interested in? Be specific to get better results."
                style={styles.textarea}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={styles.hint}>
                Example: "Track Bitcoin price movements and major market-moving news. Include analysis on why prices are changing."
              </p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Keywords (Optional)</label>
              <div style={styles.keywordContainer}>
                {formData.keywords.map((keyword) => (
                  <div key={keyword} style={styles.keyword}>
                    {keyword}
                    <span
                      onClick={() => handleRemoveKeyword(keyword)}
                      style={styles.keywordRemove}
                    >
                      <X size={14} />
                    </span>
                  </div>
                ))}
              </div>
              <div style={styles.keywordInput}>
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                  placeholder="Add a keyword and press Enter"
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  onClick={handleAddKeyword}
                  style={styles.secondaryButton}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#ffffff';
                  }}
                >
                  <Plus size={18} />
                  Add
                </button>
              </div>
              <p style={styles.hint}>
                Add specific terms to track (e.g., "BTC", "Ethereum", "SEC regulations")
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Time of Day</label>
                <input
                  type="time"
                  value={formData.time_of_day}
                  onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button
                onClick={handleCancel}
                style={styles.secondaryButton}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#cbd5e1';
                  e.target.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={styles.primaryButton}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
              >
                <Check size={18} />
                {editingTopic ? 'Update Topic' : 'Create Topic'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Topics List */}
      {topics.length === 0 ? (
        <div style={styles.empty}>
          <TrendingUp size={48} style={styles.emptyIcon} />
          <h2 style={styles.emptyTitle}>No Topics Yet</h2>
          <p style={styles.emptyText}>
            Create your first topic to start receiving automated briefings
          </p>
        </div>
      ) : (
        <div style={styles.topicsContainer}>
          {topics.map((topic) => {
            const latestBriefing = getLatestBriefing(topic.id);
            const topicBriefings = getBriefingsForTopic(topic.id);
            const unreadCount = getUnreadCount(topic.id);
            const isExpanded = expandedTopics.has(topic.id);

            return (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={styles.topicCard}
              >
                <div style={styles.topicHeader}>
                  <div style={styles.topicLeft}>
                    <h3 style={styles.topicTitle}>{topic.topic_name}</h3>
                    <div style={{
                      ...styles.activeIndicator,
                      ...(topic.is_active ? styles.activeTrue : styles.activeFalse)
                    }}>
                      {topic.is_active ? 'Active' : 'Paused'}
                    </div>
                  </div>
                  
                  <div style={styles.topicActions}>
                    <button
                      onClick={() => toggleActive(topic)}
                      style={{
                        ...styles.iconButton,
                        color: topic.is_active ? '#ef4444' : '#10b981',
                      }}
                      title={topic.is_active ? 'Pause' : 'Activate'}
                      onMouseEnter={(e) => {
                        e.target.style.background = topic.is_active ? '#fee2e2' : '#dcfce7';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8fafc';
                      }}
                    >
                      {topic.is_active ? <X size={18} /> : <Check size={18} />}
                    </button>
                    <button
                      onClick={() => handleEdit(topic)}
                      style={styles.iconButton}
                      title="Edit"
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e2e8f0';
                        e.target.style.color = '#0f172a';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8fafc';
                        e.target.style.color = '#64748b';
                      }}
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(topic.id)}
                      style={{ ...styles.iconButton, color: '#ef4444' }}
                      title="Delete"
                      onMouseEnter={(e) => {
                        e.target.style.background = '#fee2e2';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8fafc';
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {topic.description && (
                  <p style={styles.topicDescription}>{topic.description}</p>
                )}

                {topic.keywords && topic.keywords.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ ...styles.keywordContainer, marginBottom: 0 }}>
                      {topic.keywords.map((keyword) => (
                        <div key={keyword} style={styles.keyword}>
                          {keyword}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={styles.topicMeta}>
                  <div style={styles.metaItem}>
                    <Calendar size={16} />
                    {topic.frequency.charAt(0).toUpperCase() + topic.frequency.slice(1)} updates
                  </div>
                  <div style={styles.metaItem}>
                    <Clock size={16} />
                    {new Date(`2000-01-01T${topic.time_of_day}`).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  {topic.last_briefing && (
                    <div style={styles.metaItem}>
                      <Bell size={16} />
                      Last briefing: {new Date(topic.last_briefing).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Briefings Section */}
                <div style={styles.briefingsSection}>
                  <div style={styles.briefingsSectionHeader}>
                    <h4 style={styles.briefingsTitle}>
                      <FileText size={18} />
                      Briefings
                      {unreadCount > 0 && (
                        <span style={styles.unreadBadge}>{unreadCount}</span>
                      )}
                    </h4>
                    {topicBriefings.length > 1 && (
                      <button
                        onClick={() => toggleTopicExpanded(topic.id)}
                        style={styles.expandButton}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e2e8f0';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8fafc';
                        }}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp size={16} />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown size={16} />
                            Show All ({topicBriefings.length})
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {latestBriefing ? (
                    <>
                      {/* Latest Briefing Preview */}
                      <div
                        style={styles.latestBriefing}
                        onClick={() => handleBriefingClick(latestBriefing)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={styles.briefingPreviewHeader}>
                          <div style={styles.briefingDate}>
                            <Calendar size={14} />
                            {new Date(latestBriefing.created_at).toLocaleDateString()}
                          </div>
                          {!latestBriefing.is_read && (
                            <div style={{ ...styles.unreadBadge, fontSize: '0.65rem' }}>NEW</div>
                          )}
                        </div>
                        <div style={styles.briefingSummary}>
                          {latestBriefing.summary}
                        </div>
                        <div style={{
                            ...styles.keyPointsPreview,
                            animation: 'pulse 2s ease-in-out infinite',
                          }}>
                            <TrendingUp size={16} style={{ color: '#2563eb' }} />
                            <span style={{ color: '#2563eb', fontWeight: 600 }}>
                              {latestBriefing.key_points.length} key points
                            </span>
                            <span style={{ color: '#64748b' }}> • </span>
                            <span style={{ 
                              color: '#2563eb', 
                              fontWeight: 600,
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              textUnderlineOffset: '3px'
                            }}>
                              Click to read full briefing
                            </span>
                          </div>
                      </div>

                      {/* Expanded briefings list */}
                      <AnimatePresence>
                        {isExpanded && topicBriefings.length > 1 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ marginTop: '0.75rem' }}
                          >
                            <div style={styles.briefingsList}>
                              {topicBriefings.slice(1).map((briefing) => (
                                <div
                                  key={briefing.id}
                                  style={styles.briefingItem}
                                  onClick={() => handleBriefingClick(briefing)}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f8fafc';
                                    e.currentTarget.style.transform = 'translateX(4px)';
                                    e.currentTarget.querySelector('[data-arrow]').style.transform = 'translateX(4px)';
                                    e.currentTarget.querySelector('[data-arrow]').style.color = '#2563eb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#ffffff';
                                    e.currentTarget.style.transform = 'translateX(0)';
                                    e.currentTarget.querySelector('[data-arrow]').style.transform = 'translateX(0)';
                                    e.currentTarget.querySelector('[data-arrow]').style.color = '#cbd5e1';
                                  }}
                                >
                                  <div style={styles.briefingItemLeft}>
                                    <div style={styles.briefingItemTitle}>
                                      {!briefing.is_read && <span style={{ color: '#2563eb', marginRight: '0.5rem' }}>●</span>}
                                      Briefing from {new Date(briefing.created_at).toLocaleDateString()}
                                    </div>
                                    <div style={styles.briefingItemDate}>
                                      {briefing.key_points.length} key points
                                    </div>
                                  </div>
                                  <ArrowRight size={18} style={styles.briefingItemArrow} data-arrow />
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <div style={styles.noBriefings}>
                      No briefings yet. They'll appear here when generated.
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Full Briefing Modal */}
      <AnimatePresence>
        {selectedBriefing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.briefingOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedBriefing(null);
                setShowChat(false);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              style={styles.briefingModal}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setSelectedBriefing(null);
                  setShowChat(false);
                }}
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                  e.target.style.color = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f8fafc';
                  e.target.style.color = '#64748b';
                }}
              >
                <X size={20} />
              </button>

              <div style={styles.detailHeader}>
                <div style={styles.detailHeaderContent}>
                  <h1 style={styles.detailTopicName}>{selectedBriefing.topic_name}</h1>
                  <div style={styles.detailDate}>
                    <Calendar size={18} />
                    {new Date(selectedBriefing.created_at).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                
                <button
                  onClick={() => showChat ? handleCloseChat() : handleOpenChat(selectedBriefing)}
                  style={styles.generateMessageButton}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = showChat 
                      ? '0 6px 20px rgba(16, 185, 129, 0.5)'
                      : '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = showChat 
                      ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                      : '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                >
                  <MessageSquare size={20} />
                  {showChat ? 'Close Chat' : 'Generate Message'}
                </button>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                  <FileText size={20} />
                  Summary
                </h2>
                <div style={styles.summary}>{selectedBriefing.summary}</div>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>
                  <TrendingUp size={20} />
                  Key Points
                </h2>
                <ul style={styles.keyPoints}>
                  {selectedBriefing.key_points.map((point, index) => (
                    <li key={index} style={styles.keyPoint}>
                      <span style={styles.keyPointBullet}></span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              {selectedBriefing.sources && selectedBriefing.sources.length > 0 && (
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>
                    <ExternalLink size={20} />
                    Sources
                  </h2>
                  <div style={styles.sources}>
                    {selectedBriefing.sources.map((source, index) => (
                      <a
                        key={index}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.sourceItem}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <ExternalLink size={18} style={styles.sourceIcon} />
                        <div style={styles.sourceContent}>
                          <div style={styles.sourceTitle}>{source.title}</div>
                          {source.date && (
                            <div style={styles.sourceDate}>{source.date}</div>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedBriefing.analysis && (
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>
                    <Sparkles size={20} />
                    Analysis
                  </h2>
                  <div style={styles.analysis}>{selectedBriefing.analysis}</div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Chat */}
      {showChat && selectedBriefing && (
        <FloatingBriefingChat
          briefing={selectedBriefing}
          contacts={contacts}
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
};

export default TopicIntelligence;